import React from 'react';

import type { BoxProps } from '@mui/material';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListSubheader,
  Radio,
  RadioGroup,
  Rating,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { filter, includes, isNil, map, size } from 'lodash';

import { InfoOutlined, Schedule } from '@mui/icons-material';
import { TimeValidationError } from '@mui/x-date-pickers';
import { PickerChangeHandlerContext } from '@mui/x-date-pickers/internals/hooks/usePicker/usePickerValue.types';
import dayjs, { Dayjs } from 'dayjs';
import TimePickerWithShorthand, {
  TimePickerShorthand,
  TimePickerValue,
} from '../../../components/time_picker_with_shorthand';
import { createScopedI18n } from '../../../i18n/i18n';
import { TipAmountSelector } from './_tip_amount_selector';

const jobHiringActionOptionI18n = createScopedI18n('components.job_hiring_action_option.states', { joinOutput: true });

export type JobHiringActionType = 'startWork' | 'getOffWork' | 'review' | 'ban';

type StartWorkValueType = {
  type: 'startWork';
  offsetMinute: number | 'absent';
};

type GetOffWorkValueType = {
  type: 'getOffWork';
  offsetMinute: number;
};

type ReviewValueType = {
  type: 'review';
  star: number | null;
  comments: string[];
  tip?: number;
};

type BanValueType = {
  type: 'ban';
  banScope: 'restaurant' | 'chain';
  reason: string | null;
};

export type ActionValueType =
  | StartWorkValueType
  | GetOffWorkValueType
  | ReviewValueType
  | BanValueType
  | { type: null };

export type JobHiringActionOptionProps = {
  action?: JobHiringActionType | null;
  jobStartedAt?: Dayjs | string;
  jobEndedAt?: Dayjs | string;
  selection?: string[] | undefined;
  onValueChange?: (value: ActionValueType) => void;
} & BoxProps;

interface StartWorkOptionProps {
  onValueChange?: (value: StartWorkValueType) => void;
  jobStartedAt?: Dayjs | string | null;
}
type AbsentType = 'yes' | 'no';
const StartWorkOption = ({ onValueChange, jobStartedAt }: StartWorkOptionProps) => {
  const [offsetMinute, setOffsetMinute] = React.useState<TimePickerShorthand['offsetMinute']>(0);
  const [isAbsent, setIsAbsent] = React.useState<AbsentType>('no');
  const onTimePickerChanged = (datetime: TimePickerValue, context: PickerChangeHandlerContext<TimeValidationError>) => {
    if (isNil(datetime)) return;
    if (!isNil(context.validationError)) return;
    setOffsetMinute(dayjs(datetime).diff(jobStartedAt, 'minutes'));
  };

  // At the first time, we need to calls onValueChange to apply shorthand value
  React.useEffect(() => {
    if (isNil(onValueChange)) return;
    if (isAbsent === 'yes') {
      onValueChange({ type: 'startWork', offsetMinute: 'absent' });
    } else {
      onValueChange({ type: 'startWork', offsetMinute });
    }
  }, [onValueChange, offsetMinute, isAbsent]);

  return (
    <FormControl sx={{ display: 'flex', flexDirection: 'column' }}>
      <FormLabel sx={{ textAlign: 'center' }}>เวลาเข้างาน</FormLabel>

      <RadioGroup
        value={isAbsent}
        onChange={(_, entryValue) => {
          setOffsetMinute(0);
          setIsAbsent(entryValue as AbsentType);
        }}
      >
        <FormControlLabel value="no" label="มาทำงาน" control={<Radio />} />
        <FormControlLabel value="yes" label="ไม่มาทำงาน" control={<Radio />} />
      </RadioGroup>

      {isAbsent === 'no' && (
        <>
          <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
          <TimePickerWithShorthand
            shorthands={[
              { offsetMinute: 0, label: 'ตรงเวลา' },
              { offsetMinute: 15, label: 'สาย 15 นาที' },
              { offsetMinute: 30, label: 'สาย 30 นาที' },
            ]}
            value={dayjs(jobStartedAt)}
            onChange={onTimePickerChanged}
            minTime={jobStartedAt ? dayjs(jobStartedAt) : undefined}
            timeSteps={{ minutes: 15 }}
            minutesStep={15}
            desktopModeMediaQuery="@media (max-width: 1px)" // Force mobile Time Picker
            slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
          />
        </>
      )}
    </FormControl>
  );
};

interface GetOffWorkOptionProps {
  onValueChange?: (value: GetOffWorkValueType) => void;
  jobEndedAt?: Dayjs | string | null;
}
const GetOffWorkOption = ({ onValueChange, jobEndedAt }: GetOffWorkOptionProps) => {
  const [offsetMinute, setOffsetMinute] = React.useState<TimePickerShorthand['offsetMinute']>(0);
  const onTimePickerChanged = (datetime: TimePickerValue, context: PickerChangeHandlerContext<TimeValidationError>) => {
    if (isNil(datetime)) return;
    if (!isNil(context.validationError)) return;
    setOffsetMinute(dayjs(datetime).diff(jobEndedAt, 'minutes'));
  };

  // At the first time, we need to calls onValueChange to apply shorthand value
  React.useEffect(() => {
    if (isNil(onValueChange)) return;
    onValueChange({
      type: 'getOffWork',
      offsetMinute,
    });
  }, [onValueChange, offsetMinute]);

  return (
    <FormControl>
      <FormLabel sx={{ textAlign: 'center' }}>เวลาเลิกงาน</FormLabel>
      <TimePickerWithShorthand
        shorthands={[
          { offsetMinute: 0, label: 'ตรงเวลา' },
          { offsetMinute: 15, label: 'ต่อเวลา 15 นาที' },
          { offsetMinute: 30, label: 'ต่อเวลา 30 นาที' },
        ]}
        value={dayjs(jobEndedAt)}
        onChange={onTimePickerChanged}
        minTime={jobEndedAt ? dayjs(jobEndedAt) : undefined}
        timeSteps={{ minutes: 15 }}
        minutesStep={15}
        desktopModeMediaQuery="@media (max-width: 1px)" // Force mobile Time Picker
        slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
      />
    </FormControl>
  );
};

const ReviewOption = ({
  onValueChange,
  selection,
}: {
  onValueChange?: (value: ReviewValueType) => void;
  selection: string[] | undefined;
}) => {
  const [star, setStar] = React.useState<number | null>(null);
  const [comments, setComments] = React.useState<string[]>([]);
  const [tipAmount, setTipAmount] = React.useState<number>(0);
  const [totalStudents, setTotalStudents] = React.useState<number>(0);

  const starHandler = (_e: React.SyntheticEvent, value: number | null) => {
    setStar(value);
  };

  const handleTipChange = (amount: number) => {
    setTipAmount(amount);
  };

  const toggleCommentsHigherOrder = (comment: string) => () => {
    let nextComments = [...comments];
    if (includes(comments, comment)) {
      nextComments = filter(comments, (prevComment) => prevComment !== comment);
    } else {
      nextComments = [...comments, comment];
    }
    setComments(nextComments);
  };

  React.useEffect(() => {
    if (isNil(onValueChange)) return;
    onValueChange({ type: 'review', star, comments, tip: tipAmount });
  }, [star, comments, tipAmount, onValueChange, selection]);

  React.useEffect(() => {
    if (isNil(selection)) setTotalStudents(0);
    setTotalStudents(size(selection));
  }, [selection, onValueChange]);

  return (
    <Stack gap={2}>
      <Stack alignItems="center" gap={1}>
        <Rating value={star} onChange={starHandler} />
        <Typography variant="caption" color={'error'}>
          1-3 ดาว เทมป์จะได้รับบทลงโทษ
        </Typography>
      </Stack>
      <TipAmountSelector totalStudents={totalStudents} onTipChange={handleTipChange} />
      <Stack gap={1} alignItems="center">
        <Typography variant="body2">โปรดบอกความรู้สึกการปฏิบัติงานเทมป์</Typography>
        <Stack direction="row" flexWrap="wrap" rowGap={0.5} columnGap={0.25} justifyContent="space-evenly">
          {map(['ตรงต่อเวลา', 'ขยัน ตั้งใจทำงาน', 'แต่งกายเรียบร้อย บุคลิกดี', 'พูดจาสุภาพ อัธยาศัยดี'], (comment) => (
            <Chip
              key={comment}
              size="small"
              color="primary"
              label={comment}
              variant={includes(comments, comment) ? 'filled' : 'outlined'}
              onClick={toggleCommentsHigherOrder(comment)}
              sx={{ border: '0.8px solid', borderColor: 'primary.main' }}
            />
          ))}
        </Stack>
        <Stack direction="row" flexWrap="wrap" rowGap={0.5} columnGap={0.25} justifyContent="space-evenly">
          {map(['เข้างานสาย', 'แต่งกายไม่เหมาะสม', 'ละทิ้งหน้าที่ระหว่างปฎิบัติ', 'พูดจาไม่สุภาพ'], (comment) => (
            <Chip
              key={comment}
              size="small"
              color="error"
              label={comment}
              variant={includes(comments, comment) ? 'filled' : 'outlined'}
              onClick={toggleCommentsHigherOrder(comment)}
              sx={{ border: '0.8px solid', borderColor: 'error.main' }}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

const BanOption = ({ onValueChange }: { onValueChange?: (value: BanValueType) => void }) => {
  const theme = useTheme();

  const [banScope, setBanScope] = React.useState<BanValueType['banScope']>('restaurant');
  const [reason, setReason] = React.useState<string | null>(null);
  const [showReasonInfo, setShowReasonInfo] = React.useState(false);

  const reasonHandler = (_event: React.ChangeEvent | React.MouseEvent, value: string) => {
    setReason(value);
  };

  const onBanScopeChange = (_event: React.ChangeEvent | React.MouseEvent, value: string) => {
    setBanScope(value as BanValueType['banScope']);
  };

  React.useEffect(() => {
    if (isNil(onValueChange)) return;
    onValueChange({ type: 'ban', banScope, reason });
  }, [banScope, reason, onValueChange]);

  return (
    <>
      <FormControl fullWidth>
        <FormLabel sx={{ textAlign: 'center' }}>{jobHiringActionOptionI18n('ban.scope.label')}</FormLabel>
        <RadioGroup value={banScope} onChange={onBanScopeChange}>
          <FormControlLabel
            value="restaurant"
            label={jobHiringActionOptionI18n('ban.scope.restaurant')}
            control={<Radio />}
          />
          <FormControlLabel value="chain" label={jobHiringActionOptionI18n('ban.scope.chain')} control={<Radio />} />
        </RadioGroup>
      </FormControl>

      <Divider sx={{ marginY: 1 }} />

      <FormControl fullWidth>
        <FormLabel
          sx={{
            display: 'flex',
            textAlign: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {jobHiringActionOptionI18n('ban.reason.label')}
          <IconButton size="small" onClick={() => setShowReasonInfo(true)}>
            <InfoOutlined fontSize="inherit" />
          </IconButton>
        </FormLabel>
        <RadioGroup value={reason} onChange={reasonHandler}>
          <FormControlLabel
            value="unqualified"
            label={jobHiringActionOptionI18n('ban.reason.unqualified')}
            control={<Radio />}
          />
          <FormControlLabel value="other" label={jobHiringActionOptionI18n('ban.reason.other')} control={<Radio />} />
        </RadioGroup>
      </FormControl>

      <Dialog open={showReasonInfo} onClose={() => setShowReasonInfo(false)}>
        <DialogContent>
          <List
            dense
            subheader={
              <ListSubheader
                component="div"
                color="inherit"
                disableGutters
                disableSticky
                sx={{ marginLeft: -4, fontSize: 'unset' }}
              >
                {jobHiringActionOptionI18n('ban.reason.unqualified_title')}
              </ListSubheader>
            }
            sx={{ listStyleType: 'disc', listStylePosition: 'outside', marginLeft: 4 }}
          >
            <ListItem sx={{ display: 'list-item' }}>
              {jobHiringActionOptionI18n('ban.reason.unqualified_description_1')}
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              {jobHiringActionOptionI18n('ban.reason.unqualified_description_2')}
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              {jobHiringActionOptionI18n('ban.reason.unqualified_description_3')}
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              {jobHiringActionOptionI18n('ban.reason.unqualified_description_4')}
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              {jobHiringActionOptionI18n('ban.reason.unqualified_description_5')}
            </ListItem>
          </List>

          <List
            dense
            subheader={
              <ListSubheader
                component="div"
                color="inherit"
                disableGutters
                disableSticky
                sx={{ marginLeft: -4, fontSize: 'unset' }}
              >
                {jobHiringActionOptionI18n('ban.reason.other_title')}
              </ListSubheader>
            }
            sx={{ listStyleType: 'disc', listStylePosition: 'outside', marginLeft: 4 }}
          >
            <ListItem sx={{ display: 'list-item' }}>
              {jobHiringActionOptionI18n('ban.reason.other_description_1')}
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const JobHiringActionOption = ({
  selection,
  action,
  jobStartedAt,
  jobEndedAt,
  onValueChange,
  ...prop
}: JobHiringActionOptionProps) => {
  React.useEffect(() => {
    if (isNil(action) && onValueChange) onValueChange({ type: null });
  }, [action, onValueChange]);

  return (
    <Box {...prop}>
      {action === 'startWork' && <StartWorkOption onValueChange={onValueChange} jobStartedAt={jobStartedAt} />}
      {action === 'getOffWork' && <GetOffWorkOption onValueChange={onValueChange} jobEndedAt={jobEndedAt} />}
      {action === 'review' && <ReviewOption onValueChange={onValueChange} selection={selection} />}
      {action === 'ban' && <BanOption onValueChange={onValueChange} />}
    </Box>
  );
};
